import React, { useEffect, useCallback } from "react";
import Typography from "apollo-react/components/Typography";
import Box from "apollo-react/components/Box";
import Paper from "apollo-react/components/Paper";
import Button from "apollo-react/components/Button";
import Download from "apollo-react-icons/Download";
import UserIcon from 'apollo-react-icons/User';
import Avatar from 'apollo-react/components/Avatar';
import FileDocuments from 'apollo-react-icons/FileDocuments';

import { useRequestComprehensiveDetails } from "../../../hooks/useRequestComprehensiveDetails";
import axiosInstance from "../../../axiosInstance";
import { formatFileSize } from "../../../utils/formatFileSize";
import CommentsList from '../FormApproveComponents/CommentsList/CommentsList';
import CommentToggle from "../CommentToggle/CommentToggle";
import { ROLES } from "../../../constants/roles";
import { STATUSES } from "../../../constants/statuses";
import Tag from "apollo-react/components/Tag";
import makeStyles from '@mui/styles/makeStyles';

const styles = {
  commentButton: {
    border: '1px solid #0557D5 !important',
    padding: '8px 16px !important',
    fontFamily: `"Proxima Nova", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important`,
    fontSize: '14px !important',
    fontWeight: '500 !important',
    lineHeight: '1.5 !important',
    borderRadius: '4px !important',
  },
  commentButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  profileSection: {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
    border: '1px solid #E6E6E6',
    padding: '16px 48px 16px 24px',
    borderRadius: '8px',
  },
  profileAvatar: {
    backgroundColor: '#0557D5 !important',
    color: '#fff'
  },
  noItemsContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 32
},
  noItemsContainerContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  noItemsContainerIcon: {
    width: 74,
    height: 74,
    color: '#EBEBEB'
  },
  commentsSectionContainer: {
    marginTop: '35px'
  },
};

const useStyles = makeStyles(styles);

const InspectionView = ({ requestProgressId, persona, comments = [], setComments = () => {}, containerClassName = '',
role = '', committeeReviewRequestStatus = '', user = {} }) => {
  const { requestDetails, loading, error } = useRequestComprehensiveDetails(
    requestProgressId,
    persona
  );

  const classes = useStyles();
  
  const inspectionFileId = requestDetails?.request?.inspectionFile?.id;

  const userReview = user?.review?.review;
  const username = (user?.first_name && user?.last_name && `${user?.first_name} ${user?.last_name}`) ||
  (user?.username && `${user?.username}`) || '';
  const userEmail = user?.email ? `${user?.email}` : ''
  const userCategory = user?.end_users_category?.name || '';
  const userId = user?.id;
  const userReviewRejectionReason = user?.review?.rejection_reason || '';

  const fetchComments = useCallback(async () => {
    if (!inspectionFileId) return;
    const getCommentsLink = persona === "customer" ? "enduser" : "";
    const response = await axiosInstance.get(`/api/all-comments/${getCommentsLink}/inspection_file/${inspectionFileId}`);
    setComments(response.data || []);
  }, [inspectionFileId, persona]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (inspectionFileId) {
      fetchComments();
    } else {
      setComments([]);
    }
  }, [inspectionFileId, fetchComments]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileDownload = async (file) => {
    try {
      const response = await axiosInstance.get(
        `/api/files/download/${file.id}`,
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.original_name || "inspection-file");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const handleAddCommentFromCoreValidator = async (comment, subFormId, fileId = null, fileData = null) => {
    try {
        // Create FormData for file upload
        const formData = new FormData();
        formData.append("committee_member_id", userId);
        formData.append("request_progress_id", requestProgressId);
        formData.append("comment", comment);

        if (fileId) {
            formData.append("file_id", fileId);
        }

        const response = await axiosInstance.post(
            "/api/core-validator-directed-committee-comments/core",
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
        );

    } catch (error) {
        console.error("Error adding comment:", error);
        throw error;
    }

}

  // handle edit comment
  const handleEditComment = async (commentId, newText, attachmentInfo = {}) => {
    try {
      const editLink = persona === "customer" ? "enduser" : "";
      const requestData = { comment: newText };
      
      if (attachmentInfo.remove_attachment) {
        requestData.remove_attachment = true;
      } else if (attachmentInfo.new_file_id) {
        requestData.additional_file_id = attachmentInfo.new_file_id;
      }

      await axiosInstance.put(`/api/all-comments/${editLink}/inspection_file/${commentId}`, requestData);
      fetchComments();
    } catch (error) {
      console.error('error editing comment:', error);
      throw error;
    }
  };

  // handle delete comment
  const handleDeleteComment = async (commentId) => {
    try {
      const deleteLink = persona === "customer" ? "enduser" : "";
      await axiosInstance.delete(`/api/all-comments/${deleteLink}/inspection_file/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('error deleting comment:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <Paper elevation={1} sx={{ padding: 4, margin: 0 }}>
        <Typography>Loading inspection files...</Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={1} sx={{ padding: 4, margin: 0 }}>
        <Typography color="error">Error loading inspection files</Typography>
      </Paper>
    );
  }

  const inspectionFile = requestDetails?.request?.inspectionFile;

  if(!inspectionFile && role !== ROLES.core_Validator) {
    return (
      <></>
    );
  }

  return (
    <div className={containerClassName}>
      <Box sx={{ marginBottom: 4 }}>
        <Typography
          variant="title1"
          component="h2"
          sx={{
            fontWeight: 500,
            marginBottom: 3,
            color: "#333",
            paddingBottom: 1,
          }}
        >
          {userReview && <Tag label={userReview} variant={userReview === 'approved' ? 'success' : 'danger'} style={{ marginRight: 15 }} />}{userCategory}
        </Typography>
        {username && userEmail &&
          <div className={classes.profileSection}>
            <Avatar className={classes.profileAvatar} stateless><UserIcon /></Avatar>
            <div>
              <Typography variant="subtitle" emphasis="high">
                {username}
              </Typography>
              <Typography variant="caption">
                {userEmail}
              </Typography>
            </div>
          </div>
        }

        {inspectionFile ? (
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              padding: 3,
              backgroundColor: "#f9f9f9",
              marginTop: '35px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Box>
            <Typography
              variant="body1"
              sx={{fontWeight: 500 }}
            >
              {inspectionFile.original_name || "Inspection Document"}
            </Typography>

              <Typography variant="body2" color="text.secondary">
                File size:{" "}
                {inspectionFile.file_size
                  ? formatFileSize(inspectionFile.file_size)
                  : "Unknown"}
              </Typography>
            </Box>
            <Button
                variant="outlined"
                size="small"
                endIcon={<Download />}
                onClick={() => handleFileDownload(inspectionFile)}
                sx={{ marginLeft: "auto" }}
              >
                Download
            </Button>
          </Box>
        ) : (
          <Box className={classes.noItemsContainer}>
            <div className={classes.noItemsContainerContent}>
              <FileDocuments className={classes.noItemsContainerIcon} />
              <Typography variant="subtitle">
                No Inspection Files
              </Typography>
            </div>
          </Box>
        )}
      </Box>
      <div className={classes.commentsSectionContainer}>
      <CommentsList
        prevComments={comments}
        pagination={{}}
        filters={{}}
        updateFilters={() => { }}
        persona={persona}
        showControls={false}
        showFilters={false}
        showSorting={false}
        showPageSize={false}
        showResultsInfo={false}
        showPagination={false}
        showCommentsTitle={false}
        reviews={userReviewRejectionReason ? [user?.review] : []}
        onEdit={handleEditComment}
        onDelete={handleDeleteComment}
      />
      {role === ROLES.core_Validator &&
        committeeReviewRequestStatus === STATUSES.ROLE_STATUSES[ROLES.committee].ACTIVE &&
        !userReview
        &&
        <CommentToggle
          onAddComment={handleAddCommentFromCoreValidator}
          persona={persona}
          buttonText="Send Comment"
          // buttonProps={{ variant: "primary" }}
          hasComments={true}
          hideCancelButton={false}
          // buttonContainerProps={{ className: classes.commentButtonContainer }}
          hideTitleSeparator={true}
          buttonAddCommentText="Send Comment"
          headerText="Send Comment"
        />
      }
      </div>
    </div>
  );
};

export default InspectionView;
